// Implements: F-UIX-020
/**
 * A small colour dot (the mockup's `.swatch`). Pure presentation.
 *
 * The colour is data, set as `--swatch` through `style.setProperty` (the CSP forbids `style=""`).
 * Anything that is not a colour yet (unknown while the profile loads, or half-typed) removes the
 * property and draws the neutral dot instead, marked `data-empty`.
 */

import { useLayoutEffect, useRef } from "react";
import type { ReactElement } from "react";

import { parseHexColor } from "../../../shared/ui/contrast";
import styles from "./Swatch.module.css";

export interface SwatchProps {
  readonly colour: string | undefined;
}

export function Swatch({ colour }: SwatchProps): ReactElement {
  const dot = useRef<HTMLSpanElement>(null);
  const valid = colour !== undefined && parseHexColor(colour) !== null;
  useLayoutEffect(() => {
    const element = dot.current;
    if (element === null) return;
    if (valid) element.style.setProperty("--swatch", colour);
    else element.style.removeProperty("--swatch");
  }, [colour, valid]);
  return (
    <span
      ref={dot}
      className={styles.swatch}
      data-testid="design-swatch"
      data-empty={valid ? undefined : "true"}
      aria-hidden="true"
    />
  );
}
